import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudentWeeklyLearningData } from "../../../network/Endpoint";
import * as d3 from 'd3';
import memo from '../../../assets/images/s_w_Memo.png';
import accuracy from '../../../assets/images/s_w_Accuracy.png';
import clock from '../../../assets/images/s_w_Clock.png';
import grade1 from '../../../assets/images/grade_1.png';
import grade2 from '../../../assets/images/grade_2.png';
import grade3 from '../../../assets/images/grade_3.png';
import grade4 from '../../../assets/images/grade_4.png';
import grade5 from '../../../assets/images/grade_5.png';
import modeLesson from '../../../assets/images/mode_lesson.png';
import modeHomeWork from '../../../assets/images/mode_homework.png';
import modeDaily from '../../../assets/images/mode_daily.png';
import modeFree from '../../../assets/images/mode_free.png';
import modeChallenge from '../../../assets/images/mode_challeng.png';
import icoSchool from '../../../assets/images/School_ico.png';
import icoStudy from '../../../assets/images/study_ico.png';
import iconSmile from "../../../assets/images/icon-smile.svg";
import iconSmile2 from "../../../assets/images/icon-smile2.svg";
import iconSmile3 from "../../../assets/images/icon-smile3.svg";
// StudentDailyResults는 페이지 컴포넌트로 이동

const StudentWeeklyResults = ({ studentId, week, loading: parentLoading, onDateClick, selectedDate, weeklyData }) => {
  const [loading, setLoading] = useState(parentLoading || false);
  const [studentData, setStudentData] = useState(null);
  const [totalStudyTime, setTotalStudyTime] = useState(0);
  const [averageAccuracy, setAverageAccuracy] = useState(0);
  const [weeklyStageCnt, setWeeklyStageCnt] = useState(0);
  const [thisWeekRecords, setThisWeekRecords] = useState([]);
  const [thisWeekRecordCounts, setThisWeekRecordCounts] = useState({});
  const [thisWeekRecordAccuracy, setThisWeekRecordAccuracy] = useState({});
  const [thisWeekRecordStudyTime, setThisWeekRecordStudyTime] = useState({});
  const [lastWeekRecordCounts, setLastWeekRecordCounts] = useState({});
  const [lastWeekRecordAccuracy, setLastWeekRecordAccuracy] = useState({});
  const [lastWeekRecordStudyTime, setLastWeekRecordStudyTime] = useState({});
  const [weekDays, setWeekDays] = useState([]);
  const [lastWeekDays, setLastWeekDays] = useState([]);

  // 각 차트별 독립적인 비교 상태 관리
  const [compareWithLastWeekCount, setCompareWithLastWeekCount] = useState(false);
  const [compareWithLastWeekAccuracy, setCompareWithLastWeekAccuracy] = useState(false);
  const [compareWithLastWeekTime, setCompareWithLastWeekTime] = useState(false);
  
  // D3 차트를 위한 refs
  const studyCountChartRef = useRef(null);
  const accuracyChartRef = useRef(null);
  const studyTimeChartRef = useRef(null);

  // 로딩 상태 관리
  useEffect(() => {
    if (parentLoading !== undefined) {
      setLoading(parentLoading);
    }
  }, [parentLoading]);

  // 주간 날짜 계산
  useEffect(() => {
    if (week?.startDate && week?.endDate) {
      generateWeekDays();
    }
  }, [week]);

  // 월요일부터 일요일까지의 날짜 생성
  const generateWeekDays = () => {
    const days = [];
    const lastWeekDaysArray = [];
    
    if (week.startDate && week.endDate) {
      // startDate는 월요일, endDate는 일요일
      const startDate = new Date(week.startDate.replace(/\./g, '-'));
      
      // 이번주 날짜 생성
      for (let i = 0; i < 7; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);
        days.push({
          day: getDayNameInEnglish(i),
          date: currentDate.getDate(),
          fullDate: currentDate
        });
      }
      
      // 지난주 날짜 생성
      const lastWeekStartDate = new Date(startDate);
      lastWeekStartDate.setDate(startDate.getDate() - 7);
      
      for (let i = 0; i < 7; i++) {
        const lastWeekDate = new Date(lastWeekStartDate);
        lastWeekDate.setDate(lastWeekStartDate.getDate() + i);
        lastWeekDaysArray.push({
          day: getDayNameInEnglish(i),
          date: lastWeekDate.getDate(),
          fullDate: lastWeekDate
        });
      }
      
      setWeekDays(days);
      setLastWeekDays(lastWeekDaysArray);
    }
  };

  // 숫자로부터 영어 요일명 반환
  const getDayNameInEnglish = (dayNumber) => {
    const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    return days[dayNumber];
  };

  // 학생 또는 주 변경 시 차트 초기화
  useEffect(() => {
    if (studentId && week) {
      console.log("학생 또는 주 변경 감지: ", studentId, week);
      
      // 차트 초기화
      if (studyCountChartRef.current) {
        d3.select(studyCountChartRef.current).selectAll("*").remove();
      }
      if (accuracyChartRef.current) {
        d3.select(accuracyChartRef.current).selectAll("*").remove();
      }
      if (studyTimeChartRef.current) {
        d3.select(studyTimeChartRef.current).selectAll("*").remove();
      }
    }
  }, [studentId, week]);

  // 캐싱된 데이터 사용 (API 호출 대체)
  useEffect(() => {
    // 부모 컴포넌트에서 전달받은 캐싱된 데이터가 있으면 사용
    if (weeklyData && !loading) {
      processWeeklyData(weeklyData);
    } else if (!weeklyData && studentId && week?.startDate && week?.endDate) {
      // 캐싱된 데이터가 없는 경우에만 API 호출 (거의 발생하지 않음)
      fetchStudentData();
    }
  }, [weeklyData, studentId, week, loading]);
  
  // 데이터 처리 함수 (API 응답 또는 캐싱된 데이터 처리)
  const processWeeklyData = (responseData) => {
    // 기본 데이터 저장
    setStudentData(responseData);
    setTotalStudyTime(responseData.totalStudyTime);
    setAverageAccuracy(responseData.averageAccuracy);
    setWeeklyStageCnt(responseData.weeklyStageCnt);
    
    // 학습 기록 저장
    setThisWeekRecords(responseData.thisWeekRecords || []);
    
    // 이번 주 요일별 데이터
    setThisWeekRecordCounts(responseData.thisWeekRecordCounts || {});
    setThisWeekRecordAccuracy(responseData.thisWeekRecordAccuracy || {});
    setThisWeekRecordStudyTime(responseData.thisWeekRecordStudyTime || {});
    
    // 지난 주 요일별 데이터
    setLastWeekRecordCounts(responseData.lastWeekRecordCounts || {});
    setLastWeekRecordAccuracy(responseData.lastWeekRecordAccuracy || {});
    setLastWeekRecordStudyTime(responseData.lastWeekRecordStudyTime || {});
  };

  // 데이터 불러오기 (캐싱된 데이터가 없을 때만 호출)
  const fetchStudentData = async () => {
    if (!studentId || !week?.startDate || !week?.endDate) return;
    
    // 로딩 시작
    setLoading(true);
    console.log("데이터 로딩 시작: ", studentId, week);

    try {
      // API 날짜 포맷 변환 (YYYY.MM.DD -> YYYY-MM-DD)
      const convertDateFormat = (dateString) => {
        return dateString.replace(/\./g, '-');
      };
      const body = { 
        studentId: studentId,
        params: {
          startDate: convertDateFormat(week.startDate),
          endDate: convertDateFormat(week.endDate)
        }
      };
      
      const res = await getStudentWeeklyLearningData(body);
      // API 응답 데이터 처리
      if (res.data && res.data.status.code === "Success") {
        const responseData = res.data.data;
        processWeeklyData(responseData);
        
        console.log("학생 데이터 로딩 완료:", responseData.name, responseData.number);
      } else {
        console.error('API 응답 형식이 올바르지 않습니다:', res.data);
      }
    } catch (error) {
      console.error('학생 결과를 가져오는 중 오류 발생:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // 차트 데이터가 모두 준비되었을 때 차트를 그림
  useEffect(() => {
    // 로딩 중이 아니고, 모든 데이터가 있을 때만 차트 그리기
    if (!loading && 
        thisWeekRecordCounts && 
        thisWeekRecordAccuracy && 
        thisWeekRecordStudyTime && 
        Object.keys(thisWeekRecordCounts).length > 0) {  // 상위 컴포넌트에서 visibility를 관리하므로 항상 차트 준비
      
      console.log("차트 데이터 준비 완료, 렌더링 시작", 
        "비교 상태:", compareWithLastWeekCount, compareWithLastWeekAccuracy, compareWithLastWeekTime);
      
      // requestAnimationFrame 사용하여 성능 최적화
      requestAnimationFrame(() => {
        if (studyCountChartRef.current && 
            accuracyChartRef.current && 
            studyTimeChartRef.current) {
          createStudyCountChart();
          createAccuracyChart();
          createStudyTimeChart();
          console.log("차트 렌더링 완료");
        }
      });
    }
  }, [
    loading, 
    thisWeekRecordCounts, 
    thisWeekRecordAccuracy, 
    thisWeekRecordStudyTime,
    lastWeekRecordCounts,
    lastWeekRecordAccuracy,
    lastWeekRecordStudyTime, 
    compareWithLastWeekCount, 
    compareWithLastWeekAccuracy, 
    compareWithLastWeekTime,
    selectedDate  // selectedDate가 변경될 때마다 차트 다시 그리기
  ]);

  // 학습량 차트 생성
  const createStudyCountChart = () => {
    if (!studyCountChartRef.current) return;
    
    // 기존 SVG 삭제
    d3.select(studyCountChartRef.current).selectAll("*").remove();
    
    // 데이터 준비 - 이번주
    const thisWeekData = weekDays.map((item, index) => ({
      day: item.date + '일',
      count: thisWeekRecordCounts[item.day] || 0,
      isThisWeek: true,
      xIndex: index  // x축 위치 인덱스 추가
    }));
    
    // 지난주 데이터 준비 (compareWithLastWeek가 true일 때만)
    const lastWeekData = compareWithLastWeekCount ? lastWeekDays.map((item, index) => ({
      day: item.date + '일',
      count: lastWeekRecordCounts[item.day] || 0,
      isThisWeek: false,
      xIndex: index  // x축 위치 인덱스 추가
    })) : [];
    
    // X축에 표시할 데이터 준비
    let daysLabels;
    if (compareWithLastWeekCount) {
      // 비교 모드일 때: 지난주 날짜 + 이번주 날짜
      daysLabels = weekDays.map((item, index) => {
        const lastWeekDate = lastWeekDays[index].date;
        return `${lastWeekDate}일/${item.date}일`;
      });
    } else {
      // 비교 모드가 아닐 때: 이번주 날짜만
      daysLabels = thisWeekData.map(d => d.day);
    }
    
    // SVG 크기 설정
    const margin = { top: 40, right: 30, bottom: 50, left: 50 };
    const width = 880 - margin.left - margin.right;
    const height = 255 - margin.top - margin.bottom;
    
    // SVG 생성
    const svg = d3.select(studyCountChartRef.current)
      .append("svg")
      .attr("width", 880)
      .attr("height", 255)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // X축 스케일 설정 - 요일 인덱스 기준
    const x = d3.scaleBand()
      .domain(d3.range(7)) // 0-6 인덱스 사용 (월-일)
      .range([0, width])
      .padding(0.5);
    
    // 막대 너비 - 항상 고정 24px
    const barWidth = 24;
    
    // 막대 위치 계산 - 중앙 정렬을 위한 오프셋
    const barGap = 12; // 막대 간 간격 12px
    const barOffset = compareWithLastWeekCount 
      ? (x.bandwidth() - (barWidth * 2 + barGap)) / 2  // 비교 모드일 때 두 개의 막대 중앙 정렬 (간격 고려)
      : (x.bandwidth() - barWidth) / 2;     // 비교 모드가 아닐 때 한 개의 막대 중앙 정렬
    
    // Y축 스케일 설정 - 최댓값을 찾아 10개 단위로 올림
    const maxValue = Math.max(
      d3.max(thisWeekData, d => d.count) || 10,
      compareWithLastWeekCount ? (d3.max(lastWeekData, d => d.count) || 0) : 0
    );
    const yMax = Math.ceil(maxValue / 10) * 10;
    const y = d3.scaleLinear()
      .domain([0, yMax])
      .range([height, 0]);
    
    // 수평 그리드 라인 그리기
    svg.selectAll("line.horizontalGrid")
      .data(y.ticks(5))
      .enter()
      .append("line")
      .attr("class", "horizontalGrid")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // Y축 눈금과 레이블 설정
    svg.selectAll(".y-label")
      .data(y.ticks(5))
      .enter()
      .append("text")
      .attr("class", "y-label")
      .attr("x", -10)
      .attr("y", d => y(d))
      .attr("dy", "0.32em")
      .attr("text-anchor", "end")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .text(d => d + "개");
    
    // X축 그리기 (바닥 선만)
    svg.append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", height)
      .attr("y2", height)
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // X축 레이블
    svg.selectAll(".x-label")
      .data(daysLabels)
      .enter()
      .append("text")
      .attr("class", "x-label")
      .attr("x", (d, i) => x(i) + x.bandwidth() / 2)
      .attr("y", height + 30)
      .attr("text-anchor", "middle")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .text(d => d);
    
    // 비교 표시가 활성화된 경우 지난주 막대 그리기
    if (compareWithLastWeekCount) {
      // 지난주 막대
      svg.selectAll(".bar-last-week")
        .data(lastWeekData)
        .enter()
        .append("path")
        .attr("class", "bar-last-week")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.count === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset;
          const y0 = y(d.count);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);

          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#DBE3FF"); // 지난주 학습량 색상
      
      // 지난주 값 표시 - 다시 추가
      svg.selectAll(".bar-value-last-week")
        .data(lastWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value-last-week")
        .attr("x", d => x(d.xIndex) + barOffset + barWidth / 2)
        .attr("y", d => y(d.count) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#b1b1b1")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.count);
      
      // 이번주 막대 (위치 조정)
      svg.selectAll(".bar-this-week")
        .data(thisWeekData)
        .enter()
        .append("path")
        .attr("class", "bar-this-week")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.count === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset + (compareWithLastWeekCount ? barWidth + barGap : 0);
          const y0 = y(d.count);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);
          
          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#627bff");
      
      // 이번주 값 표시
      svg.selectAll(".bar-value-this-week")
        .data(thisWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value-this-week")
        .attr("x", d => x(d.xIndex) + barOffset + (compareWithLastWeekCount ? barWidth + barGap + barWidth/2 : barWidth / 2))
        .attr("y", d => y(d.count) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#355BDF")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.count);
        
      // 범례 추가
      const legend = svg.append("g")
        .attr("transform", `translate(${width - 200}, -30)`);
      
      // 이번주 범례
      legend.append("rect")
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", "#627bff")
        .attr("rx", 2)
        .attr("ry", 2);
      
      legend.append("text")
        .attr("x", 20)
        .attr("y", 12)
        .style("font-size", "12px")
        .attr("fill", "#667085")
        .text("이번주");
      
      // 지난주 범례
      legend.append("rect")
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", "#DBE3FF")
        .attr("rx", 2)
        .attr("ry", 2)
        .attr("transform", "translate(80, 0)");
      
      legend.append("text")
        .attr("x", 100)
        .attr("y", 12)
        .style("font-size", "12px")
        .attr("fill", "#667085")
        .text("지난주");
    } else {
      // 비교 모드가 아닌 경우, 원래처럼 이번주 막대만 그리기
      svg.selectAll(".bar")
        .data(thisWeekData)
        .enter()
        .append("path")
        .attr("class", "bar")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.count === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset;
          const y0 = y(d.count);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius =Math.min(12, barWidth / 2, barHeight);

          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#627bff");
      
      // 학습량 - 비교 모드가 아닌 경우 값 표시
      svg.selectAll(".bar-value")
        .data(thisWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value")
        .attr("x", d => x(d.xIndex) + barOffset + barWidth / 2)
        .attr("y", d => y(d.count) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#355BDF")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.count);
    }
  };

  // 정확도 차트 생성
  const createAccuracyChart = () => {
    if (!accuracyChartRef.current) return;
    
    // 기존 SVG 삭제
    d3.select(accuracyChartRef.current).selectAll("*").remove();
    
    // 데이터 준비 - 이번주
    const thisWeekData = weekDays.map((item, index) => ({
      day: item.date + '일',
      accuracy: thisWeekRecordAccuracy[item.day] || 0,
      isThisWeek: true,
      xIndex: index  // x축 위치 인덱스 추가
    }));
    
    // 지난주 데이터 준비 (compareWithLastWeekAccuracy가 true일 때만)
    const lastWeekData = compareWithLastWeekAccuracy ? lastWeekDays.map((item, index) => ({
      day: item.date + '일',
      accuracy: lastWeekRecordAccuracy[item.day] || 0,
      isThisWeek: false,
      xIndex: index  // x축 위치 인덱스 추가
    })) : [];
    
    // X축에 표시할 데이터 준비
    let daysLabels;
    if (compareWithLastWeekAccuracy) {
      // 비교 모드일 때: 지난주 날짜 + 이번주 날짜
      daysLabels = weekDays.map((item, index) => {
        const lastWeekDate = lastWeekDays[index].date;
        return `${lastWeekDate}일/${item.date}일`;
      });
    } else {
      // 비교 모드가 아닐 때: 이번주 날짜만
      daysLabels = thisWeekData.map(d => d.day);
    }
    
    // SVG 크기 설정
    const margin = { top: 40, right: 30, bottom: 50, left: 50 };
    const width = 880 - margin.left - margin.right;
    const height = 255 - margin.top - margin.bottom;
    
    // SVG 생성
    const svg = d3.select(accuracyChartRef.current)
      .append("svg")
      .attr("width", 880)
      .attr("height", 255)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // X축 스케일 설정 - 요일 인덱스 기준
    const x = d3.scaleBand()
      .domain(d3.range(7)) // 0-6 인덱스 사용 (월-일)
      .range([0, width])
      .padding(0.5);
    
    // 막대 너비 - 항상 고정 24px
    const barWidth = 24;
    
    // 막대 위치 계산 - 중앙 정렬을 위한 오프셋
    const barGap = 12; // 막대 간 간격 12px
    const barOffset = compareWithLastWeekAccuracy 
      ? (x.bandwidth() - (barWidth * 2 + barGap)) / 2  // 비교 모드일 때 두 개의 막대 중앙 정렬 (간격 고려)
      : (x.bandwidth() - barWidth) / 2;     // 비교 모드가 아닐 때 한 개의 막대 중앙 정렬
    
    // Y축 스케일 설정
    const y = d3.scaleLinear()
      .domain([0, 100])
      .range([height, 0]);
    
    // 수평 그리드 라인 그리기
    const yTicks = [0, 20, 40, 60, 80, 100];
    svg.selectAll("line.horizontalGrid")
      .data(yTicks)
      .enter()
      .append("line")
      .attr("class", "horizontalGrid")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // Y축 눈금과 레이블 설정
    svg.selectAll(".y-label")
      .data(yTicks)
      .enter()
      .append("text")
      .attr("class", "y-label")
      .attr("x", -10)
      .attr("y", d => y(d))
      .attr("dy", "0.32em")
      .attr("text-anchor", "end")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .text(d => d + "%");
    
    // X축 그리기 (바닥 선만)
    svg.append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", height)
      .attr("y2", height)
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // X축 레이블
    svg.selectAll(".x-label")
      .data(daysLabels)
      .enter()
      .append("text")
      .attr("class", "x-label")
      .attr("x", (d, i) => x(i) + x.bandwidth() / 2)
      .attr("y", height + 30)
      .attr("text-anchor", "middle")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .text(d => d);
    
    // 비교 표시가 활성화된 경우 지난주 막대 그리기
    if (compareWithLastWeekAccuracy) {
      // 지난주 막대
      svg.selectAll(".bar-last-week")
        .data(lastWeekData)
        .enter()
        .append("path")
        .attr("class", "bar-last-week")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.accuracy === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset;
          const y0 = y(d.accuracy);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);
          
          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#DBEFED"); // 지난주 정확도 색상
      
      // 지난주 값 표시 - 다시 추가
      svg.selectAll(".bar-value-last-week")
        .data(lastWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value-last-week")
        .attr("x", d => x(d.xIndex) + barOffset + barWidth / 2)
        .attr("y", d => y(d.accuracy) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#b1b1b1")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.accuracy);
      
      // 이번주 막대 (위치 조정)
      svg.selectAll(".bar-this-week")
        .data(thisWeekData)
        .enter()
        .append("path")
        .attr("class", "bar-this-week")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.accuracy === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset + (compareWithLastWeekAccuracy ? barWidth + barGap : 0);
          const y0 = y(d.accuracy);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);
          
          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#4cd3a5");
      
      // 이번주 값 표시
      svg.selectAll(".bar-value-this-week")
        .data(thisWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value-this-week")
        .attr("x", d => x(d.xIndex) + barOffset + (compareWithLastWeekAccuracy ? barWidth + barGap + barWidth/2 : barWidth / 2))
        .attr("y", d => y(d.accuracy) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#008979")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.accuracy);
        
      // 범례 추가
      const legend = svg.append("g")
        .attr("transform", `translate(${width - 200}, -30)`);
      
      // 이번주 범례
      legend.append("rect")
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", "#4cd3a5")
        .attr("rx", 2)
        .attr("ry", 2);
      
      legend.append("text")
        .attr("x", 20)
        .attr("y", 12)
        .style("font-size", "12px")
        .attr("fill", "#667085")
        .text("이번주");
      
      // 지난주 범례
      legend.append("rect")
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", "#DBEFED")
        .attr("rx", 2)
        .attr("ry", 2)
        .attr("transform", "translate(80, 0)");
      
      legend.append("text")
        .attr("x", 100)
        .attr("y", 12)
        .style("font-size", "12px")
        .attr("fill", "#667085")
        .text("지난주");
    } else {
      // 비교 모드가 아닌 경우, 원래처럼 이번주 막대만 그리기
      svg.selectAll(".bar")
        .data(thisWeekData)
        .enter()
        .append("path")
        .attr("class", "bar")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.accuracy === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset;
          const y0 = y(d.accuracy);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);
          
          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#4cd3a5");
      
      // 정확도 - 값 표시
      svg.selectAll(".bar-value")
        .data(thisWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value")
        .attr("x", d => x(d.xIndex) + barOffset + barWidth / 2)
        .attr("y", d => y(d.accuracy) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#008979")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.accuracy);
    }
  };

  // 학습 시간 차트 생성
  const createStudyTimeChart = () => {
    if (!studyTimeChartRef.current) return;
    
    // 기존 SVG 삭제
    d3.select(studyTimeChartRef.current).selectAll("*").remove();
    
    // 데이터 준비 - 이번주
    const thisWeekData = weekDays.map((item, index) => ({
      day: item.date + '일',
      time: Math.ceil(thisWeekRecordStudyTime[item.day] / 60) || 0, // 초를 분으로 변환
      isThisWeek: true,
      xIndex: index  // x축 위치 인덱스 추가
    }));
    
    // 지난주 데이터 준비 (compareWithLastWeekTime가 true일 때만)
    const lastWeekData = compareWithLastWeekTime ? lastWeekDays.map((item, index) => ({
      day: item.date + '일',
      time: Math.ceil(lastWeekRecordStudyTime[item.day] / 60) || 0, // 초를 분으로 변환
      isThisWeek: false,
      xIndex: index  // x축 위치 인덱스 추가
    })) : [];
    
    // X축에 표시할 데이터 준비
    let daysLabels;
    if (compareWithLastWeekTime) {
      // 비교 모드일 때: 지난주 날짜 + 이번주 날짜
      daysLabels = weekDays.map((item, index) => {
        const lastWeekDate = lastWeekDays[index].date;
        return `${lastWeekDate}일/${item.date}일`;
      });
    } else {
      // 비교 모드가 아닐 때: 이번주 날짜만
      daysLabels = thisWeekData.map(d => d.day);
    }
    
    // SVG 크기 설정
    const margin = { top: 40, right: 30, bottom: 50, left: 50 };
    const width = 880 - margin.left - margin.right;
    const height = 255 - margin.top - margin.bottom;
    
    // SVG 생성
    const svg = d3.select(studyTimeChartRef.current)
      .append("svg")
      .attr("width", 880)
      .attr("height", 255)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // X축 스케일 설정 - 요일 인덱스 기준
    const x = d3.scaleBand()
      .domain(d3.range(7)) // 0-6 인덱스 사용 (월-일)
      .range([0, width])
      .padding(0.5);
    
    // 막대 너비 - 항상 고정 24px
    const barWidth = 24;
    
    // 막대 위치 계산 - 중앙 정렬을 위한 오프셋
    const barGap = 12; // 막대 간 간격 12px
    const barOffset = compareWithLastWeekTime 
      ? (x.bandwidth() - (barWidth * 2 + barGap)) / 2  // 비교 모드일 때 두 개의 막대 중앙 정렬 (간격 고려)
      : (x.bandwidth() - barWidth) / 2;     // 비교 모드가 아닐 때 한 개의 막대 중앙 정렬
    
    // Y축 스케일 설정 - 최대값에 따라 간격 조정
    const maxTime = Math.max(
      d3.max(thisWeekData, d => d.time) || 0,
      compareWithLastWeekTime ? (d3.max(lastWeekData, d => d.time) || 0) : 0
    );
    
    // Y축 최대값 및 간격 설정 (5분 미만: 1분 간격, 10분 미만: 2분 간격, 그 이상: 적절한 간격)
    let yMax, yTickInterval;
    
    if (maxTime < 5) {
      // 5분 미만: 1분 간격, 최소 5분까지 표시
      yMax = 5;
      yTickInterval = 1;
    } else if (maxTime < 10) {
      // 10분 미만: 2분 간격
      yMax = Math.ceil(maxTime / 2) * 2;
      yTickInterval = 2;
    } else if (maxTime < 30) {
      // 30분 미만: 5분 간격
      yMax = Math.ceil(maxTime / 5) * 5;
      yTickInterval = 5;
    } else {
      // 30분 이상: 10분 간격
      yMax = Math.ceil(maxTime / 10) * 10;
      yTickInterval = 10;
    }
    
    const y = d3.scaleLinear()
      .domain([0, yMax])
      .range([height, 0]);
    
    // 수평 그리드 라인 그리기 - 간격 조정된 눈금 사용
    const yTicks = d3.range(0, yMax + yTickInterval, yTickInterval);
    svg.selectAll("line.horizontalGrid")
      .data(yTicks)
      .enter()
      .append("line")
      .attr("class", "horizontalGrid")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // Y축 눈금과 레이블 설정
    svg.selectAll(".y-label")
      .data(yTicks)
      .enter()
      .append("text")
      .attr("class", "y-label")
      .attr("x", -10)
      .attr("y", d => y(d))
      .attr("dy", "0.32em")
      .attr("text-anchor", "end")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .text(d => d + "분");
    
    // X축 그리기 (바닥 선만)
    svg.append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", height)
      .attr("y2", height)
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // X축 레이블
    svg.selectAll(".x-label")
      .data(daysLabels)
      .enter()
      .append("text")
      .attr("class", "x-label")
      .attr("x", (d, i) => x(i) + x.bandwidth() / 2)
      .attr("y", height + 30)
      .attr("text-anchor", "middle")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .text(d => d);
    
    // 비교 표시가 활성화된 경우 지난주 막대 그리기
    if (compareWithLastWeekTime) {
      // 지난주 막대
      svg.selectAll(".bar-last-week")
        .data(lastWeekData)
        .enter()
        .append("path")
        .attr("class", "bar-last-week")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.time === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset;
          const y0 = y(d.time);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);
          
          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#FFEFCA"); // 지난주 학습시간 색상
      
      // 지난주 값 표시 - 다시 추가
      svg.selectAll(".bar-value-last-week")
        .data(lastWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value-last-week")
        .attr("x", d => x(d.xIndex) + barOffset + barWidth / 2)
        .attr("y", d => y(d.time) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#b1b1b1")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.time);
      
      // 이번주 막대 (위치 조정)
      svg.selectAll(".bar-this-week")
        .data(thisWeekData)
        .enter()
        .append("path")
        .attr("class", "bar-this-week")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.time === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset + (compareWithLastWeekTime ? barWidth + barGap : 0);
          const y0 = y(d.time);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);
          
          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#ffbf3e");
      
      // 이번주 값 표시
      svg.selectAll(".bar-value-this-week")
        .data(thisWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value-this-week")
        .attr("x", d => x(d.xIndex) + barOffset + (compareWithLastWeekTime ? barWidth + barGap + barWidth/2 : barWidth / 2))
        .attr("y", d => y(d.time) - 10)
        .attr("text-anchor", "middle")
          .attr("fill", "#FF8C00")
          .style("font-size", "12px")
          .style("font-weight", "500")
        .text(d => d.time);
        
      // 범례 추가
      const legend = svg.append("g")
        .attr("transform", `translate(${width - 200}, -30)`);
      
      // 이번주 범례
      legend.append("rect")
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", "#ffbf3e")
        .attr("rx", 2)
        .attr("ry", 2);
      
      legend.append("text")
        .attr("x", 20)
        .attr("y", 12)
        .style("font-size", "12px")
        .attr("fill", "#667085")
        .text("이번주");
      
      // 지난주 범례
      legend.append("rect")
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", "#FFEFCA")
        .attr("rx", 2)
        .attr("ry", 2)
        .attr("transform", "translate(80, 0)");
      
      legend.append("text")
        .attr("x", 100)
        .attr("y", 12)
        .style("font-size", "12px")
        .attr("fill", "#667085")
        .text("지난주");
    } else {
      // 비교 모드가 아닌 경우, 원래처럼 이번주 막대만 그리기
      svg.selectAll(".bar")
        .data(thisWeekData)
        .enter()
        .append("path")
        .attr("class", "bar")
        .attr("d", d => {
          // 값이 0이면 path 없이 반환
          if (d.time === 0) return "";
          
          const x0 = x(d.xIndex) + barOffset;
          const y0 = y(d.time);
          const barHeight = height - y0;
          // 막대 높이가 24px 미만이면 둥근 모서리 없음, 그 이상이면 둥근 모서리 적용
          const cornerRadius = Math.min(12, barWidth / 2, barHeight);
          
          if (cornerRadius === 0) {
            // 둥근 모서리 없는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0}
              L ${x0 + barWidth},${y0}
              L ${x0 + barWidth},${height}
              Z
            `;
          } else {
            // 둥근 모서리 있는 직사각형
            return `
              M ${x0},${height}
              L ${x0},${y0 + cornerRadius}
              Q ${x0},${y0} ${x0 + cornerRadius},${y0}
              L ${x0 + barWidth - cornerRadius},${y0}
              Q ${x0 + barWidth},${y0} ${x0 + barWidth},${y0 + cornerRadius}
              L ${x0 + barWidth},${height}
              Z
            `;
          }
        })
        .attr("fill", "#ffbf3e");
      
      // 학습 시간 - 값 표시
      svg.selectAll(".bar-value")
        .data(thisWeekData)
        .enter()
        .append("text")
        .attr("class", "bar-value")
        .attr("x", d => x(d.xIndex) + barOffset + barWidth / 2)
        .attr("y", d => y(d.time) - 10)
        .attr("text-anchor", "middle")
        .attr("fill", "#FF8C00")
        .style("font-size", "12px")
        .style("font-weight", "500")
        .text(d => d.time);
    }
  };

  // 학습 시간을 포맷팅하는 함수 (초 -> 분:초 또는 초)
  const formatStudyTime = (seconds) => {
    if (!seconds || seconds === 0) return '0초';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    // 1분 미만은 초만 표시
    if (minutes < 1) {
      return `${remainingSeconds}초`;
    }
    
    // 1분 이상은 분:초 표시
    return `${minutes}분 ${remainingSeconds}초`;
  };

  // 요일 이름을 한글로 변환
  const getDayNameInKorean = (dayName) => {
    const dayMap = {
      'MONDAY': '월',
      'TUESDAY': '화',
      'WEDNESDAY': '수',
      'THURSDAY': '목',
      'FRIDAY': '금',
      'SATURDAY': '토',
      'SUNDAY': '일'
    };
    return dayMap[dayName] || dayName;
  };

  // 정확도, 학습시간 차트 컨테이너 UI
  const renderChartContainer = (title, subtitle, chartRef, index, isCompareEnabled = true) => {
    let compareState, setCompareState;
    
    // 차트 유형에 따라 적절한 상태와 상태 변경 함수 할당
    if (index === 0) { // 학습량 차트
      compareState = compareWithLastWeekCount;
      setCompareState = setCompareWithLastWeekCount;
    } else if (index === 1) { // 정확도 차트
      compareState = compareWithLastWeekAccuracy;
      setCompareState = setCompareWithLastWeekAccuracy;
    } else if (index === 2) { // 학습시간 차트
      compareState = compareWithLastWeekTime;
      setCompareState = setCompareWithLastWeekTime;
    }
    
    return (
      <div className="mb-12" key={`chart-${index}-${selectedDate ? 'hidden' : 'visible'}`}>
        {/* 제목 행 */}
        <div className="flex items-center mb-4">
          <div className="flex-1"></div>
          <h3 className="text-[24px] font-[700] text-[#50555F] text-center flex-1">
            {title} {subtitle && <span className="text-[16px] font-[500] text-[#868E9F]">({subtitle})</span>}
          </h3>
          <div className="flex-1 flex justify-end">
            {isCompareEnabled && (
              <div className="flex items-center">
                <span className="text-[#50555F] mr-2 text-[16px] font-[500]">지난주와 비교하기</span>
                <button 
                  type="button"
                  className={`w-[67.5px] h-[30px] rounded-full flex items-center px-1 cursor-pointer transition-colors duration-300 ${compareState ? 'bg-[#FFDC72]' : 'bg-[#D8D8D8]'}`}
                  onClick={() => {
                    console.log(`${index} 차트 토글 전 상태:`, compareState);
                    const newState = !compareState;
                    setCompareState(newState);
                    console.log(`${index} 차트 토글 후 상태:`, newState);
                  }}
                >
                  <div 
                    className={`w-[34.5px] h-[34.5px] rounded-full bg-white transform transition-transform duration-300 ${
                      compareState ? 'translate-x-[29px]' : 'translate-x-[-4px]'
                    }`}
                    style={{ filter: 'drop-shadow(0px 1.5px 7.5px rgba(0, 0, 0, 0.20))' }}
                  ></div>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* 차트 컨테이너 */}
        <div className="rounded-lg p-4 bg-white flex justify-center items-center">
          <div className="w-[880px] h-[255px]" ref={chartRef}></div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-500">데이터를 불러올 수 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-[1044px] mx-auto  pb-8">
        {/* 요일별 학습 횟수 표시는 별도 컴포넌트로 분리하여 상위에서 배치 */}

        {/* 주간 학습 현황 */}
        <div className="bg-white p-[22px] rounded-lg shadow-lg mb-8" key="weekly-results-summary">
          <h2 className="text-[24px] text-[#50555F] font-bold mb-6 text-center mt-5">주간 총 학습 현황</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-11 mt-14">
            {/* 학습량 */}
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <img src={memo} alt="학습량" className="w-[104px] h-[104px] mr-9"/>
                <div>
                  <h3 className="text-[24px] font-[500] text-[#50555F]">학습량</h3>
                  <div className="text-[34px] font-[700] text-[#355BDF]">{weeklyStageCnt}<span
                      className="text-[24px] ml-1">개</span></div>
                </div>
              </div>
            </div>

            {/* 정확도 */}
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <img src={accuracy} alt="정확도" className="w-[104px] h-[104px] mr-9"/>
                <div>
                  <h3 className="text-[24px] font-[500] text-[#50555F]">정확도</h3>
                  <div className="text-[34px] font-[700] text-[#355BDF]">{Math.round(averageAccuracy) || 0}<span
                      className="text-[24px] ml-1">%</span></div>
                </div>
              </div>
            </div>

            {/* 학습시간 */}
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <img src={clock} alt="학습시간" className="w-[104px] h-[104px] mr-9"/>
                <div>
                  <h3 className="text-[24px] font-[500] text-[#50555F]">학습시간</h3>
                  <div className="text-[34px] font-[700] text-[#355BDF]">
                    {Math.floor(totalStudyTime / 60) > 0 ? Math.floor(totalStudyTime / 60) : "00"}
                    <span className="text-[24px]">분</span>
                    {totalStudyTime % 60 > 0 ? totalStudyTime % 60 : "00"}
                    <span className="text-[24px]">초</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 차트 섹션 */}
          <div className="border-t pt-6" key="charts-container">
            {renderChartContainer('학습량', '스테이지 개수', studyCountChartRef, 0)}
            {renderChartContainer('정확도', '', accuracyChartRef, 1)}
            {renderChartContainer('학습시간', '분', studyTimeChartRef, 2)}
          </div>


        {/* 학습 모드별 결과 상세 */}
        <div className="border-t pt-8 mb-8">
          <h3 className="text-[24px] font-[700] text-[#50555F] text-center mb-10">학습 모드별 상세결과</h3>

          {/* 학습 기록이 없을 때 메시지 표시 */}
          {thisWeekRecords && thisWeekRecords.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-10 bg-gray-50 rounded-lg">
                <div className="text-[20px] text-gray-500 mb-4">이번 주 학습 기록이 없습니다.</div>
                <div className="text-[16px] text-gray-400">학습을 완료하면 여기에 결과가 표시됩니다.</div>
              </div>
          ) : (
              <>
                {/* 학습 기록이 있지만 학교 학습과 자율 학습 모두 표시할 것이 없을 때 */}
                {thisWeekRecords.filter(record => record.sourceType === 'HOMEWORK' || record.sourceType === 'LESSON').length === 0 &&
                    thisWeekRecords.filter(record => record.sourceType !== 'HOMEWORK' && record.sourceType !== 'LESSON').length === 0 && (
                        <div className="flex flex-col items-center justify-center p-10 bg-gray-50 rounded-lg">
                          <div className="text-[20px] text-gray-500 mb-4">분류할 수 있는 학습 기록이 없습니다.</div>
                          <div className="text-[16px] text-gray-400">학습을 완료하면 여기에 결과가 표시됩니다.</div>
                        </div>
                    )}

                {/* 학교 학습 영역 - 항상 표시 */}
                {thisWeekRecords.length > 0 && (
                    <div className="mb-8 mx-[5px]">
                      <div className="flex mb-8 ml-12 gap-1">
                        <img className="flex items-center w-[32px] h-[32px]" src={icoSchool} alt="school icon"/>
                        <div className="flex items-end text-[20px] font-[700] text-[#50555F]">학교 학습</div>
                      </div>

                      {/* 학교 학습 기록이 있으면 테이블 표시, 없으면 메시지 표시 */}
                      {thisWeekRecords.filter(record => record.sourceType === 'HOMEWORK' || record.sourceType === 'LESSON').length > 0 ? (
                          <div className="overflow-x-auto rounded-t-[20px] overflow-hidden">
                            <table className="min-w-full bg-white border rounded-lg table-fixed">
                              <thead
                                  className="bg-[#F3F7FF] text-[18px] font-[700] text-[#9BA5C2] h-[56px] border border-[#F3F7FF]">
                              <tr className="border-b-0 text-center">
                                <th className="py-3 w-[70px]">순번</th>
                                <th className="py-3 w-[110px]">학습 일시</th>
                                <th className="py-3 w-[110px]">학습 모드</th>
                                <th className="py-3 w-[130px]">차시 단원명</th>
                                <th className="py-3 w-[300px]">차시명/스테이지명</th>
                                <th className="py-3 w-[100px]">학습 시간</th>
                                <th className="py-3 w-[80px]">등급</th>
                                <th className="py-3 w-[90px]">정확도</th>
                              </tr>
                              </thead>
                              <tbody>
                              {thisWeekRecords
                                  .filter(record => record.sourceType === 'HOMEWORK' || record.sourceType === 'LESSON')
                                  .sort((a, b) => {
                                    // 1. 모드 우선순위 정의 (LESSON 먼저)
                                    const priorityMap = {'LESSON': 1, 'HOMEWORK': 2};
                                    const priorityA = priorityMap[a.sourceType] || 99;
                                    const priorityB = priorityMap[b.sourceType] || 99;

                                    // 2. 우선순위로 정렬
                                    if (priorityA !== priorityB) {
                                      return priorityA - priorityB;
                                    }

                                    // 3. 같은 우선순위면 날짜로 정렬
                                    return new Date(a.dateTime) - new Date(b.dateTime);
                                  })
                                  .map((record, index, array) => {
                                    // 현재 레코드의 sourceType
                                    const currentSourceType = record.sourceType;

                                    // 이전 레코드의 sourceType (없으면 null)
                                    const prevSourceType = index > 0 ? array[index - 1].sourceType : null;

                                    // 새로운 모드 섹션의 시작인지 확인
                                    const isNewSection = currentSourceType !== prevSourceType;

                                    // 현재 섹션 내에서의 순번 계산
                                    const sectionRecords = array.filter((r, i) => i <= index && r.sourceType === currentSourceType);
                                    const sectionIndex = sectionRecords.length;

                                    // 날짜 포맷팅 (월일 시:분 표시)
                                    const formatTime = (dateTimeStr) => {
                                      if (!dateTimeStr) return '';

                                      const date = new Date(dateTimeStr);
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const hours = String(date.getHours()).padStart(2, '0');
                                      const minutes = String(date.getMinutes()).padStart(2, '0');

                                      return `${month}월${day}일\n${hours}:${minutes}`;
                                    };

                                    return (
                                        <tr
                                            key={index}
                                            className={`${isNewSection && index > 0 ? 'border-t-2 border-gray-300' : ''}`}
                                        >
                                                                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[16px] text-[#5E6169]">{sectionIndex}</td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169] whitespace-pre-line">{formatTime(record.dateTime)}</td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                          <div className="flex justify-center">
                                            <img
                                                src={
                                                  record.sourceType === 'LESSON' ? modeLesson :
                                                      record.sourceType === 'HOMEWORK' ? modeHomeWork :
                                                          record.sourceType === 'DAILY' ? modeDaily :
                                                              record.sourceType === 'FREE' ? modeFree :
                                                                  record.sourceType === 'CHALLENGE' ? modeChallenge : modeLesson
                                                }
                                                alt={record.sourceType}
                                            />
                                          </div>
                                        </td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center truncate text-[14px] text-[#5E6169]"
                                            title={record.lessonChapterTitle}>{record.lessonChapterTitle}</td>
                                        <td className="py-3 px-2 border-b border-r border-[#E9E9E9] text-center">
                                          <div className="truncate w-[284px] text-[14px] text-[#5E6169]"
                                               title={record.lessonTitle}>{record.lessonTitle}</div>
                                          <div className="truncate w-[284px] text-[14px] text-[#5E6169]"
                                               title={record.stageName}>{record.stageName}</div>
                                        </td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169]">{formatStudyTime(record.studySeconds || 0)}</td>
                                          <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                            {record.grade && (
                                                <div className="flex items-center justify-center">
                                                  <img
                                                      src={
                                                        record.grade === 1 ? grade1 :
                                                            record.grade === 10 ? grade2 :
                                                                record.grade === 30 ? grade3 :
                                                                    record.grade === 50 ? grade4 :
                                                                        record.grade === 100 ? grade5 : grade5
                                                      }
                                                      alt={`Grade ${record.grade}`}
                                                  />
                                                </div>
                                            )}
                                          </td>
                                          <td className="py-1 px-1 border-b border-[#E9E9E9] text-center text-[#5E6169] text-[14px] font-[700]">
                                            {record.accuracy ? (
                                                <div className="flex flex-col items-center justify-center">
                                                  <img
                                                      src={
                                                        record.accuracy >= 90 ? iconSmile :
                                                            record.accuracy >= 70 ? iconSmile2 :
                                                                iconSmile3
                                                      }
                                                      alt="accuracy icon"
                                                      className="w-8 h-8"
                                                  />
                                                  <span
                                                      style={{
                                                        color: record.accuracy >= 90 ? '#7494FF' :
                                                            record.accuracy >= 70 ? '#00B079' :
                                                                '#EF7070'
                                                      }}
                                                  >
                                                  {record.accuracy.toFixed(0)}%
                                                </span>
                                                </div>
                                            ) : '-'}
                                          </td>
                                        </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                      ) : (
                          <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
                            <div className="text-[18px] text-gray-500">이번 주 학교 학습 기록이 없습니다.</div>
                          </div>
                      )}
                    </div>
                )}

                {/* 자율 학습 영역 - 항상 표시 */}
                {thisWeekRecords.length > 0 && (
                    <div className="mb-8 mx-[5px] pt-8">
                      <div className="flex items-center">
                        <div className="flex mb-8 ml-12 gap-1">
                          <img className="flex items-center w-[32px] h-[32px]" src={icoStudy} alt="study icon"/>
                          <div className="flex items-end text-[20px] font-[700] text-[#50555F]">자율 학습</div>
                        </div>
                      </div>

                      {/* 자율 학습 기록이 있으면 테이블 표시, 없으면 메시지 표시 */}
                      {thisWeekRecords.filter(record => record.sourceType !== 'HOMEWORK' && record.sourceType !== 'LESSON').length > 0 ? (
                          <div className="overflow-x-auto rounded-t-[20px] overflow-hidden">
                            <table className="min-w-full bg-white border rounded-lg table-fixed">
                              <thead
                                  className="bg-[#F3F7FF] text-[18px] font-[700] text-[#9BA5C2] h-[56px] border border-[#F3F7FF]">
                              <tr className="border-b-0 text-center">
                                <th className="py-3 w-[70px]">순번</th>
                                <th className="py-3 w-[110px]">학습 일시</th>
                                <th className="py-3 w-[110px]">학습 모드</th>
                                <th className="py-3 w-[130px]">차시 단원명</th>
                                <th className="py-3 w-[300px]">차시명/스테이지명</th>
                                <th className="py-3 w-[100px]">학습 시간</th>
                                <th className="py-3 w-[80px]">등급</th>
                                <th className="py-3 w-[90px]">정확도</th>
                              </tr>
                              </thead>
                              <tbody>
                              {thisWeekRecords
                                  .filter(record => record.sourceType !== 'HOMEWORK' && record.sourceType !== 'LESSON')
                                  .sort((a, b) => {
                                    // 1. 모드 우선순위 정의
                                    const priorityMap = {'DAILY': 1, 'FREE': 2, 'CHALLENGE': 3};
                                    const priorityA = priorityMap[a.sourceType] || 99;
                                    const priorityB = priorityMap[b.sourceType] || 99;

                                    // 2. 우선순위로 정렬
                                    if (priorityA !== priorityB) {
                                      return priorityA - priorityB;
                                    }

                                    // 3. 같은 우선순위면 날짜로 정렬
                                    return new Date(a.dateTime) - new Date(b.dateTime);
                                  })
                                  .map((record, index, array) => {
                                    // 현재 레코드의 sourceType
                                    const currentSourceType = record.sourceType;

                                    // 이전 레코드의 sourceType (없으면 null)
                                    const prevSourceType = index > 0 ? array[index - 1].sourceType : null;

                                    // 새로운 모드 섹션의 시작인지 확인
                                    const isNewSection = currentSourceType !== prevSourceType;

                                    // 현재 섹션 내에서의 순번 계산
                                    const sectionRecords = array.filter((r, i) => i <= index && r.sourceType === currentSourceType);
                                    const sectionIndex = sectionRecords.length;

                                    // 날짜 포맷팅 (월일 시:분 표시)
                                    const formatTime = (dateTimeStr) => {
                                      if (!dateTimeStr) return '';

                                      const date = new Date(dateTimeStr);
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const hours = String(date.getHours()).padStart(2, '0');
                                      const minutes = String(date.getMinutes()).padStart(2, '0');

                                      return `${month}월${day}일\n${hours}:${minutes}`;
                                    };

                                    return (
                                        <tr
                                            key={index}
                                            className={`${isNewSection && index > 0 ? 'border-t-2 border-gray-300' : ''}`}
                                        >
                                                                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[16px] text-[#5E6169]">{sectionIndex}</td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169] whitespace-pre-line">{formatTime(record.dateTime)}</td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                          <div className="flex justify-center">
                                            <img
                                                src={
                                                  record.sourceType === 'LESSON' ? modeLesson :
                                                      record.sourceType === 'HOMEWORK' ? modeHomeWork :
                                                          record.sourceType === 'DAILY' ? modeDaily :
                                                              record.sourceType === 'FREE' ? modeFree :
                                                                  record.sourceType === 'CHALLENGE' ? modeChallenge : modeLesson
                                                }
                                                alt={record.sourceType}
                                            />
                                          </div>
                                        </td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center truncate text-[14px] text-[#5E6169]"
                                            title={`${record.level}레벨${record.chapterSeq}단원`}>{record.level}레벨{record.chapterSeq}단원
                                        </td>
                                        <td className="py-4 px-2 border-b border-r border-[#E9E9E9] text-center">
                                          <div className="truncate w-[284px] text-[14px] text-[#5E6169]"
                                               title={record.stageName}>{record.stageName}</div>
                                        </td>
                                        <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169]">{formatStudyTime(record.studySeconds || 0)}</td>
                                          <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                            {record.grade && (
                                                <div className="flex items-center justify-center">
                                                  <img
                                                      src={
                                                        record.grade === 1 ? grade1 :
                                                            record.grade === 10 ? grade2 :
                                                                record.grade === 30 ? grade3 :
                                                                    record.grade === 50 ? grade4 :
                                                                        record.grade === 100 ? grade5 : grade5
                                                      }
                                                      alt={`Grade ${record.grade}`}
                                                  />
                                                </div>
                                            )}
                                          </td>
                                          <td className="py-1 px-1 border-b border-[#E9E9E9] text-center text-[#5E6169] text-[14px] font-[700]">
                                            {record.accuracy ? (
                                                <div className="flex flex-col items-center justify-center">
                                                  <img
                                                      src={
                                                        record.accuracy >= 90 ? iconSmile :
                                                            record.accuracy >= 70 ? iconSmile2 :
                                                                iconSmile3
                                                      }
                                                      alt="accuracy icon"
                                                      className="w-8 h-8"
                                                  />
                                                  <span
                                                      style={{
                                                        color: record.accuracy >= 90 ? '#7494FF' :
                                                            record.accuracy >= 70 ? '#00B079' :
                                                                '#EF7070'
                                                      }}
                                                  >
                                                  {record.accuracy.toFixed(0)}%
                                                </span>
                                                </div>
                                            ) : '-'}
                                          </td>
                                        </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                      ) : (
                          <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
                            <div className="text-[18px] text-gray-500">이번 주 자율 학습 기록이 없습니다.</div>
                          </div>
                      )}
                    </div>
                )}
              </>
          )}

        </div>
        </div>
      </div>
    </div>
  );
};

export default StudentWeeklyResults; 